/**
 * [TD-68 Section 2.6 / Verdict 6.A] Dashboard Join-by-Code modal.
 *
 * Opens from the `dashboard-join-by-code-btn` sibling of `New League` in
 * the league-grid header. Single input → POST → navigate to
 * /league/{joined.id}/lobby. Toast on 404. Auto-defaults the team name
 * via the backend's `JoinLeagueRequest.team_name = Optional[None]`
 * fallback (`{first_name}'s Team`) - no client-side name prompt v1.0.
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { LogIn, X } from "lucide-react";
import { toast } from "sonner";
import { authHeaders } from "../../contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function JoinByCodeModal({ open, onClose, isDark }) {
  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const inputRef = useRef(null);
  const nav = useNavigate();

  useEffect(() => {
    if (open) {
      setCode("");
      // Auto-focus input after the modal mounts.
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const trimmed = code.trim();
  const canSubmit = trimmed.length > 0 && !submitting;

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const { data } = await axios.post(
        `${API}/leagues/join-by-code/${encodeURIComponent(trimmed)}`,
        { team_name: undefined }, // backend auto-defaults
        { headers: authHeaders() },
      );
      const leagueId = data?.league_id;
      const leagueName = data?.league_name || "league";
      toast.success(`Joined ${leagueName}`);
      onClose();
      if (leagueId) {
        nav(`/league/${leagueId}/lobby`);
      } else {
        // Defensive: existing /join-by-code returned only {message}; the
        // TD-68 patch adds league_id but if a stale build is in flight
        // fall back to the dashboard refresh.
        nav("/dashboard");
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 404) {
        toast.error("Invalid invite code");
      } else if (detail && detail.includes("hasn't opened the lobby")) {
        // [P2] The code is good, the lobby just is not open yet. Say that and
        // send them somewhere useful instead of leaving them in the modal.
        toast.error(detail, { duration: 7000 });
        onClose();
        nav("/dashboard");
      } else {
        toast.error(detail || "Could not join league");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const cardBg = isDark ? "bg-[#1C1C1C] border-[#252525]" : "bg-white border-black/[0.10]";
  const text = isDark ? "text-[#F5F5F5]" : "text-[#0A0A0A]";
  const muted = isDark ? "text-[rgba(255,255,255,0.55)]" : "text-[rgba(0,0,0,0.55)]";
  const inputCls = isDark
    ? "bg-[#111111] border-[#252525] text-[#F5F5F5] placeholder-[#A0A0A0]/40"
    : "bg-[#F0F0F0] border-black/[0.10] text-[#0A0A0A] placeholder-black/30";

  return (
    <div
      data-testid="join-by-code-modal-root"
      className="fixed inset-0 z-50 flex items-center justify-center px-4"
    >
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />
      <div
        className={`relative w-full max-w-md rounded-2xl border p-6 shadow-2xl ${cardBg}`}
      >
        <button
          data-testid="join-by-code-close-btn"
          onClick={onClose}
          className={`absolute top-3 right-3 ${muted} hover:text-[#FF6B00] transition-colors`}
          aria-label="Close"
        >
          <X size={18} />
        </button>
        <div className="flex items-center gap-2 mb-1">
          <LogIn size={16} className="text-[#FF6B00]" />
          <h2 className={`font-display font-bold text-lg uppercase ${text}`}>Join by Code</h2>
        </div>
        <p className={`font-body text-sm mb-5 ${muted}`}>
          Paste the invite code your commissioner shared.
        </p>
        <input
          ref={inputRef}
          data-testid="join-by-code-input"
          type="text"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && canSubmit) submit();
          }}
          placeholder="invite-code"
          inputMode="text"
          autoComplete="off"
          spellCheck={false}
          autoCapitalize="off"
          maxLength={32}
          className={`w-full rounded-lg border px-3 py-3 font-mono text-base outline-none focus:border-[#FF6B00] transition-colors ${inputCls}`}
        />
        <div className="flex items-center gap-2 mt-4">
          <button
            data-testid="join-by-code-cancel-btn"
            onClick={onClose}
            className={`flex-1 py-2.5 rounded-lg font-display font-bold text-sm uppercase tracking-wider transition-all ${
              isDark ? "bg-white/[0.06] hover:bg-white/[0.12] text-[#F5F5F5]" : "bg-black/[0.06] hover:bg-black/[0.12] text-[#0A0A0A]"
            }`}
          >
            Cancel
          </button>
          <button
            data-testid="join-by-code-submit"
            disabled={!canSubmit}
            onClick={submit}
            className="flex-1 py-2.5 rounded-lg bg-[#FF6B00] hover:bg-[#FF8533] disabled:opacity-40 disabled:cursor-not-allowed text-white font-display font-black uppercase tracking-wider text-sm transition-all active:scale-95"
          >
            {submitting ? "Joining..." : "Join"}
          </button>
        </div>
      </div>
    </div>
  );
}
